// Money on a claim: the strip at the top, and the assessment (claimed vs SWI) with deposit, retention and balance.
import { money, pct, type Assessment } from '../../lib/claimCalc';
import type { ClaimModel } from '../../lib/claimData';

const F = (n: number | null | undefined) => (n === null || n === undefined ? '—' : money(n));

export function MoneyStrip({ m, pbsTotal, certified }: { m: ClaimModel; pbsTotal: number | null; certified: boolean }) {
  const C = m.claimed;
  const S = m.swi;
  const diff = C.payableVip - S.payableVip;
  const linesDiff = m.lines.filter((l) => (l.swi_pct ?? l.claimed_pct) !== l.claimed_pct).length;
  const excluded = m.vars.filter((v) => v.swi < (v.row.claimed_now ?? 0)).length;
  const off = pbsTotal !== null && Math.abs(pbsTotal - C.payableVip) > 1;
  return (
    <div className="money-strip">
      <div className="money">
        <span>Claimed this period · FJD VIP</span>
        <b>{F(C.payableVip)}</b>
        <span className={off ? 'warn-t' : ''}>{pbsTotal === null ? 'Worked out from the lines' : off ? `PBS document says ${F(pbsTotal)}` : 'Matches PBS document'}</span>
      </div>
      <div className="money">
        <span>{certified ? 'Certified · FJD VIP' : 'SWI recommends · FJD VIP'}</span>
        <b className="good-t">{F(S.payableVip)}</b>
        <span>{certified ? 'Signed by SWI and the architect' : 'Draft — not yet signed'}</span>
      </div>
      <div className="money">
        <span>Difference</span>
        <b className={diff > 0.005 ? 'bad-t' : ''}>{diff > 0.005 ? `− ${F(diff)}` : '—'}</b>
        <span>
          {linesDiff} line{linesDiff === 1 ? '' : 's'} differ{linesDiff === 1 ? 's' : ''}
          {excluded ? ` · ${excluded} variation${excluded === 1 ? '' : 's'} excluded` : ''}
        </span>
      </div>
      {m.isDeposit ? (
        <div className="money">
          <span>Deposit · FJD VEP</span>
          <b>{F(S.totalClaim)}</b>
          <span>{pct(m.terms.deposit_pct)} of contract = {F(m.depositDefault)} · recovered {pct(m.terms.deposit_recovery_pct)} per claim</span>
        </div>
      ) : (
      <div className="money">
        <span>Variations verified · FJD VEP</span>
        <b>{F(S.variations)}</b>
        <span className={m.varClaimed > m.varSwi ? 'bad-t' : ''}>{m.varClaimed > m.varSwi ? `PBS claims ${F(m.varClaimed)}` : m.vars.length ? 'All verified' : 'None in this claim'}</span>
      </div>
      )}
      <div className="money">
        <span>Retention held after this claim · FJD</span>
        <b>{F(S.retention.toDate)}</b>
        <span>
          Limit {money(S.retention.limit)} · {money(S.retention.remaining)} to go
        </span>
      </div>
    </div>
  );
}

function Row({ l, a, b, bold, neg, sub }: { l: string; a: number; b: number; bold?: boolean; neg?: boolean; sub?: boolean }) {
  const f = (n: number) => (neg ? `− ${money(n)}` : money(n));
  return (
    <tr className={bold ? 'sub2' : sub ? 'ind' : ''}>
      <td>{l}</td>
      <td className="right">{f(a)}</td>
      <td className="right">{f(b)}</td>
    </tr>
  );
}

export function Assessment({ m }: { m: ClaimModel }) {
  const C: Assessment = m.claimed;
  const S: Assessment = m.swi;
  const t = m.terms;
  const varPct = (v: number) => (m.approvedVariations ? `${money((v / m.approvedVariations) * 100)} %` : '');
  return (
    <div className="assess">
      <div className="assess-main">
        <table className="t">
          <thead>
            <tr>
              <th />
              <th className="right">PBS claimed</th>
              <th className="right">SWI recommends</th>
            </tr>
          </thead>
          <tbody>
            {m.isDeposit ? (
              <>
                <Row l={`Deposit / advance payment (VEP) — contract deposit ${pct(t.deposit_pct)} = ${money(m.depositDefault)}`} a={C.totalClaim} b={S.totalClaim} bold />
                <Row l={C.retention.thisClaim || S.retention.thisClaim ? `Less retention ${pct(t.retention_pct)} of the deposit` : 'Retention — not taken from the deposit'} a={C.retention.thisClaim} b={S.retention.thisClaim} neg />
              </>
            ) : (
              <>
                <Row l="Valuation of works this period (VEP)" a={C.works} b={S.works} />
                <Row l="Variations on account (VEP)" a={C.variations} b={S.variations} />
                <Row l="Total value this period (VEP)" a={C.totalVep} b={S.totalVep} bold />
                <Row l={`Less deposit recovered this claim (${pct(t.deposit_recovery_pct)} of deposit)`} a={C.deposit.thisClaim} b={S.deposit.thisClaim} neg />
                <Row l="Total claim (VEP)" a={C.totalClaim} b={S.totalClaim} bold />
                <Row l={`Less retention ${pct(t.retention_pct)} of total claim`} a={C.retention.thisClaim} b={S.retention.thisClaim} neg />
              </>
            )}
            <Row l="Total payable (VEP)" a={C.payableVep} b={S.payableVep} />
            <Row l={`Add VAT ${t.vat_pct ?? 12.5} %`} a={C.vat} b={S.vat} />
            <tr className="tot">
              <td>Total payable (VIP)</td>
              <td className="right">{money(C.payableVip)}</td>
              <td className="right good-t">{money(S.payableVip)}</td>
            </tr>
          </tbody>
        </table>
        <p className="muted small" style={{ padding: '6px 12px', margin: 0 }}>
          Running totals start {m.beforeLabel}.
        </p>
      </div>
      <div className="assess-side">
        <div className="k">Contract deposit</div>
        <table className="t">
          <tbody>
            <tr>
              <td>{m.isDeposit ? 'Deposit paid by this claim' : S.deposit.paid ? 'Deposit paid (deposit claim)' : `Deposit paid — ${pct(t.deposit_pct)} of contract`}</td>
              <td className="right">{money(S.deposit.full)}</td>
            </tr>
            <tr className="ind">
              <td>Recovered before this claim</td>
              <td className="right">− {money(S.deposit.before)}</td>
            </tr>
            <tr className="ind">
              <td>Recovered this claim</td>
              <td className="right">− {money(S.deposit.thisClaim)}</td>
            </tr>
            <tr className="sub2">
              <td>Balance of deposit</td>
              <td className="right">{money(S.deposit.balance)}</td>
            </tr>
          </tbody>
        </table>
        <div className="k">Retention · {pct(t.retention_pct)} per claim, up to the limit</div>
        <table className="t">
          <tbody>
            <tr>
              <td>Limit — {pct(t.retention_limit_pct)} of contract + approved variations</td>
              <td className="right">{money(S.retention.limit)}</td>
            </tr>
            <tr className="ind">
              <td>Held before this claim</td>
              <td className="right">{money(S.retention.before)}</td>
            </tr>
            <tr className="ind">
              <td>This claim (SWI)</td>
              <td className="right">+ {money(S.retention.thisClaim)}</td>
            </tr>
            <tr className="sub2">
              <td>Held to date</td>
              <td className="right">{money(S.retention.toDate)}</td>
            </tr>
            <tr className="ind">
              <td>Remaining to limit</td>
              <td className="right">{money(S.retention.remaining)}</td>
            </tr>
          </tbody>
        </table>
        {S.retention.limit > 0 ? (
          <div className="track" role="img" aria-label={`Retention held to date is ${Math.round((S.retention.toDate / S.retention.limit) * 100)} % of the limit`}>
            <i style={{ width: `${Math.min(100, (S.retention.before / S.retention.limit) * 100)}%`, background: '#7a5200' }} />
            <i style={{ width: `${Math.min(100, (S.retention.thisClaim / S.retention.limit) * 100)}%`, background: 'var(--orange)' }} />
          </div>
        ) : null}
        <div className="k">Variations on account</div>
        <table className="t">
          <tbody>
            <tr>
              <td>Approved in register</td>
              <td className="right">{money(m.approvedVariations)}</td>
            </tr>
            <tr className="ind">
              <td>Claimed before</td>
              <td className="right">
                {money(m.variationsBefore)} <span className="muted">{varPct(m.variationsBefore)}</span>
              </td>
            </tr>
            <tr className="ind">
              <td>This claim (SWI)</td>
              <td className="right">
                {money(S.variations)} <span className="muted">{varPct(S.variations)}</span>
              </td>
            </tr>
            <tr className="sub2">
              <td>Claimed to date</td>
              <td className="right">
                {money(S.toDate.variations_vep)} <span className="muted">{varPct(S.toDate.variations_vep)}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="assess-foot">
        <div className="money">
          <span>Contract sum</span>
          <b>{money(t.sum_vep)}</b>
          <span>VEP · {money(t.sum_vep * (1 + (t.vat_pct ?? 12.5) / 100))} VIP at {t.vat_pct ?? 12.5} %</span>
        </div>
        <div className="money">
          <span>+ Variations on account to date</span>
          <b>{money(S.balance.variationsToDate)}</b>
          <span>VEP</span>
        </div>
        <div className="money">
          <span>Paid to date incl. this (VEP)</span>
          <b>{money(S.balance.paid)}</b>
          <span>{S.balance.depositPaid ? `incl. deposit ${money(S.balance.depositPaid)} · retention ${money(S.balance.retention)} · deposit still to recover ${money(S.deposit.balance)}` : `plus retention ${money(S.balance.retention)} · deposit ${money(S.balance.deposit)}`}</span>
        </div>
        <div className="money">
          <span>Balance of contract</span>
          <b>{money(S.balance.balance)}</b>
          <span>VEP · still to certify</span>
        </div>
      </div>
    </div>
  );
}
